import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import type { CorrecaoTexto } from "@/hooks/useCorretorTexto";

interface Props {
  textareaRef: RefObject<HTMLTextAreaElement>;
  texto: string;
  correcoes: CorrecaoTexto[];
  onAplicar: (correcao: CorrecaoTexto, sugestao: string) => void;
}

const ESTILOS_COPIADOS = [
  "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
  "fontFamily", "fontSize", "fontWeight", "lineHeight", "letterSpacing", "wordSpacing", "tabSize",
] as const;

export function CorretorSugestoes({ textareaRef, texto, correcoes, onAplicar }: Props) {
  const espelhoRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const validas = correcoes.filter(c => c.sugestoes.length > 0);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const esp = espelhoRef.current;
    if (!ta || !esp) return;
    const cs = getComputedStyle(ta);
    for (const k of ESTILOS_COPIADOS) esp.style[k] = cs[k];
    esp.style.borderStyle = "solid";
    esp.style.borderColor = "transparent";
    esp.scrollTop = ta.scrollTop;
  });

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const lerCursor = () => setCursor(document.activeElement === ta && ta.selectionStart === ta.selectionEnd ? ta.selectionStart : null);
    const rolar = () => { if (espelhoRef.current) espelhoRef.current.scrollTop = ta.scrollTop; };
    const sair = () => setTimeout(() => setCursor(null), 150);
    ta.addEventListener("keyup", lerCursor);
    ta.addEventListener("click", lerCursor);
    ta.addEventListener("focus", lerCursor);
    ta.addEventListener("blur", sair);
    ta.addEventListener("scroll", rolar);
    return () => {
      ta.removeEventListener("keyup", lerCursor);
      ta.removeEventListener("click", lerCursor);
      ta.removeEventListener("focus", lerCursor);
      ta.removeEventListener("blur", sair);
      ta.removeEventListener("scroll", rolar);
    };
  }, [textareaRef]);

  const ativa = cursor === null ? null : validas.find(c => cursor >= c.offset && cursor <= c.offset + c.length) ?? null;

  const trechos: ReactNode[] = [];
  let pos = 0;
  for (const c of [...validas].sort((a, b) => a.offset - b.offset)) {
    if (c.offset < pos) continue;
    trechos.push(texto.slice(pos, c.offset));
    trechos.push(
      <span
        key={c.offset}
        className="underline decoration-wavy decoration-primary/70 decoration-[1.5px] underline-offset-[3px]"
      >
        {texto.slice(c.offset, c.offset + c.length)}
      </span>,
    );
    pos = c.offset + c.length;
  }
  trechos.push(texto.slice(pos));

  return (
    <>
      <div
        ref={espelhoRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-transparent"
      >
        {trechos}
        {"​"}
      </div>
      {ativa && (
        <div className="absolute bottom-full left-2 z-20 mb-1.5 flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-lg border border-border-warm bg-surface px-1.5 py-1 text-[12px] shadow-md">
          {ativa.sugestoes.slice(0, 3).map(s => (
            <button
              key={s}
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                onAplicar(ativa, s);
              }}
              className="rounded-md px-2 py-0.5 font-semibold text-primary hover:bg-primary/10"
            >
              {s}
            </button>
          ))}
          <span className="truncate pl-1 pr-1 text-[11px] text-muted-foreground">{ativa.mensagem}</span>
        </div>
      )}
    </>
  );
}
